import {
  AriaTabPanel,
  AriaTabs,
  KdsButton,
  KdsCallout,
  KdsCardFile,
  KdsDivider,
  KdsDropfile,
  KdsInput,
  KdsLabel,
  KdsModal,
  KdsTab,
  KdsTabList,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  RiAddCircleFill,
  RiDeleteBinLine,
  RiInformationLine,
  RiResetLeftLine,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { sumBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useToasts } from "@/hooks";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { type ModalEditVersionsProps } from "./ModalEditVersions.types";

export const ModalEditVersions = ({
  isOpen,
  activeTab,
  isModificationRequest,
  onOpenChange,
}: ModalEditVersionsProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  // File Upload Hooks
  const fileContext = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  // Form context
  const methods = useFormContext<OrderItemConfigurationSchema>();
  const versionsMethods = useForm<{
    versions: OrderItemConfigurationSchema["versions"];
  }>({
    defaultValues: { versions: methods.getValues("versions") },
  });

  // States
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [currentActiveTab, setCurrentActiveTab] = useState<string>(
    activeTab ?? "tab_0"
  );

  // Redefine variables
  const versions = versionsMethods.watch().versions;

  // Reset default values
  // Avoid added version still not saved with autosave in the parent page
  useEffect(
    () => versionsMethods.reset({ versions: methods.getValues("versions") }),
    [methods.getValues, versionsMethods.reset]
  );

  // handle Version File upload
  const handleFileUpload = async ({
    file,
    versionIndex,
  }: {
    file: File;
    versionIndex: number;
  }) => {
    const uploadDetails = await createFileUploadUrl(
      "sandboxItemVersion.file",
      file
    );
    const newSignedId = uploadDetails?.fileSignedId;
    fileContext?.pushFile(newSignedId, {
      fileName: file.name,
      size: file.size,
      contentType: file.type,
    });
    await uploadFile(file, uploadDetails);
    fileContext?.setFileUploadState(newSignedId, "uploaded");
    versionsMethods.setValue(
      `versions.${versionIndex}.fileSignedId`,
      newSignedId
    );
    versionsMethods.setValue(`versions.${versionIndex}.fileName`, file.name);
    versionsMethods.setValue(`versions.${versionIndex}.size`, file.size);
  };

  // Handle Add version
  const handleAddVersion = () => {
    const {
      dispatches,
      name: _name,
      id: _id,
      ...rest
    } = versionsMethods.getValues("versions").filter((v) => !v.isDeleted)[0];

    const newVersion = {
      id: undefined,
      tempId: crypto.randomUUID(),
      name: `V${versionsMethods.watch("versions").length + 1}`,
      fileSignedId: "",
      isDeleted: false,
      blobId: "",
      fileName: "",
      size: undefined,
      url: "",
      dispatches: dispatches.map((dispatch) => {
        const { quantity: _, ...restDispatch } = dispatch;
        return { quantity: 0, ...restDispatch };
      }),
      ...rest,
    };

    versionsMethods.setValue("versions", [...versions, newVersion], {
      shouldDirty: true,
    });

    setCurrentActiveTab(
      `tab_${versionsMethods.getValues("versions").length - 1}`
    );
  };

  // Handle Save
  const handleSave = () => {
    setIsSaving(true);

    setTimeout(() => {
      methods.setValue(
        "versions",
        versionsMethods
          .getValues("versions")
          .filter((v) => !(!v.id && v.isDeleted)),
        { shouldDirty: true }
      );
      methods.setValue(
        "totalQuantity",
        sumBy(
          methods.getValues("versions").filter((v) => !v.isDeleted),
          (version) => sumBy(version.dispatches, "quantity")
        ),
        { shouldDirty: true }
      );
      setIsSaving(false);
      add({
        state: "success",
        title: t("orderItemConfig.modal.editVersions.toastSuccess"),
      });
      onOpenChange();
    }, 500);
  };

  return (
    <KdsModal
      size="md"
      dialogProps={{
        dialogHeaderProps: {
          title: t("orderItemConfig.modal.editVersions.title"),
          description: t("orderItemConfig.modal.editVersions.subtitle"),
        },
        content: (
          <div>
            <div className="mb-sp-md flex items-center justify-between">
              <div className="flex flex-nowrap items-center gap-sp-xs">
                <KdsText color="text-bodyvariant">
                  {t("orderItemConfig.totalVersion")}
                </KdsText>
                <KdsText size="subtitle-sm">
                  {versions.filter((v) => !v.isDeleted).length}
                </KdsText>
              </div>

              <KdsButton
                iconLeft={RiAddCircleFill}
                variant="outlined"
                color="neutral"
                onPress={handleAddVersion}
              >
                {t("tableVersions.addVersion")}
              </KdsButton>
            </div>
            <AriaTabs
              selectedKey={currentActiveTab}
              onSelectionChange={(k) => setCurrentActiveTab(k as string)}
            >
              <KdsTabList fullWidth withBorder alignLeft>
                {versions.map((version, index) => {
                  return (
                    <KdsTab
                      badgeType={version.isDeleted ? "error" : undefined}
                      key={version.id ?? version.tempId}
                      id={`tab_${index}`}
                      label={t("orderItemConfig.modal.editVersions.tabName", {
                        number: index + 1,
                      })}
                    />
                  );
                })}
              </KdsTabList>

              {versions.map((version, index) => (
                <AriaTabPanel
                  key={version.id ?? version.tempId}
                  id={`tab_${index}`}
                >
                  <div className="pt-sp-xl">
                    {version.isDeleted && (
                      <div className="pb-sp-md">
                        <KdsCallout
                          title={
                            isModificationRequest
                              ? t(
                                  "orderItemConfig.modal.editVersions.calloutModificationRequestRestoreTitle"
                                )
                              : t(
                                  "orderItemConfig.modal.editVersions.calloutRestoreTitle"
                                )
                          }
                          iconLeft={RiInformationLine}
                          state="error"
                          has="button"
                          buttonProps={{
                            variant: "outlined",
                            color: "neutral",
                            size: "small",
                            iconLeft: RiResetLeftLine,
                            children: t("buttons.restore"),
                            onPress: () =>
                              versionsMethods.setValue(
                                `versions.${index}.isDeleted`,
                                false
                              ),
                          }}
                          position="right"
                        />
                      </div>
                    )}

                    <div className="pb-sp-md">
                      <KdsLabel>
                        {t(
                          "orderItemConfig.modal.editVersions.labelVersionName"
                        )}
                      </KdsLabel>
                      <KdsInput
                        disabled={version.isDeleted}
                        hasError={
                          !!versionsMethods.formState.errors.versions &&
                          !!versionsMethods.formState.errors.versions[index]
                            ?.name
                        }
                        {...versionsMethods.register(`versions.${index}.name`)}
                      />
                    </div>

                    <div className="pb-sp-md">
                      <KdsLabel>
                        {t(
                          "orderItemConfig.modal.editVersions.labelVersionFile"
                        )}
                      </KdsLabel>
                      {version.fileSignedId ||
                      (!version.isHDFileDeleted && version.blobId) ? (
                        <KdsCardFile
                          isDisabled={version.isDeleted}
                          fileName={
                            versionsMethods.watch(`versions.${index}`).fileName
                          }
                          fileUrl={
                            versionsMethods.watch(`versions.${index}`).url
                          }
                          fileSize={
                            versionsMethods.watch(`versions.${index}`).size
                          }
                          isDeletable
                          isViewable={
                            !!versionsMethods.watch(`versions.${index}`).url
                          }
                          onDelete={() => {
                            versionsMethods.setValue(
                              `versions.${index}.isHDFileDeleted`,
                              !!version.blobId
                            );
                            versionsMethods.setValue(
                              `versions.${index}.fileSignedId`,
                              undefined
                            );
                          }}
                        />
                      ) : (
                        <Controller
                          control={versionsMethods.control}
                          name={`versions.${index}.fileSignedId`}
                          render={({ field, fieldState }) => (
                            <KdsDropfile
                              isDisabled={version.isDeleted}
                              errorMessage={getDropfileErrorMessage(
                                fieldState.error?.message
                              )}
                              hasError={!!fieldState.error}
                              isDeletable={!version.isDeleted && !!field.value}
                              isLoading={isUploadingFile}
                              isReadable={!version.isDeleted && !!field.value}
                              maxSize={SIZE_5G}
                              onDelete={() => field.onChange("")}
                              onUploadError={() =>
                                versionsMethods.setValue(
                                  `versions.${index}.fileSignedId`,
                                  ""
                                )
                              }
                              onUploadStart={() => {
                                setIsUploadingFile(true);
                                versionsMethods.clearErrors(
                                  `versions.${index}.fileSignedId`
                                );
                              }}
                              onUpload={(file: File) =>
                                void handleFileUpload({
                                  file: file,
                                  versionIndex: index,
                                })
                              }
                              onUploadSuccess={() => setIsUploadingFile(false)}
                              placeholderDescription={t(
                                "orderItemConfig.modal.editVersions.filePlaceholderDescription"
                              )}
                              placeholderTitle={t(
                                "orderItemConfig.modal.editVersions.filePlaceholderTitle"
                              )}
                            />
                          )}
                        />
                      )}
                    </div>

                    <KdsDivider />

                    <div className="pt-sp-md">
                      {versions.filter((v) => !v.isDeleted).length > 1 &&
                        !version.isDeleted && (
                          <KdsButton
                            variant="ghost"
                            color="error"
                            iconLeft={RiDeleteBinLine}
                            onPress={() =>
                              versionsMethods.setValue(
                                `versions.${index}.isDeleted`,
                                true
                              )
                            }
                          >
                            {t("buttons.deleteVersion")}
                          </KdsButton>
                        )}
                    </div>
                  </div>
                </AriaTabPanel>
              ))}
            </AriaTabs>
          </div>
        ),
        dialogFooterProps: {
          type: "double",
          buttonProps: [
            {
              variant: "outlined",
              color: "neutral",
              children: t("buttons.cancel"),
              onPress: onOpenChange,
            },

            {
              children: t("buttons.save"),
              isLoading: isSaving,
              onPress: handleSave,
            },
          ],
        },
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    />
  );
};
